import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, shareReplay } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { IRoom } from 'src/app/models/rooms';
import { ContentLanguageService } from '../content-language.service';
import { IInstitutionHeader } from 'src/app/models/organization.models';
import { RoomStudentDetail } from 'src/app/models/student-detail.models';

export interface User {
  first_name: string;
  email: `${string}@${string}`;
  last_name: string;
  is_subscribed: boolean;
  progress_percentage: string;
  id: string;
  phone?: string;
  image_profile?: string;
  image_profile_url?: string;
  progress?:
    | number
    | {
        progress_percentage?: number;
        children_completed_1?: number;
        children_total_1?: number;
        chapters?: { id: number; percentage: number }[];
      };
  has_generated_report?: boolean;
  /** Display-only label produced by class-users-list-page to render the row. */
  last_login_label?: string | null;
  /** Active chapter the student is currently working on (new students endpoint). */
  current_chapter?: {
    order: number;
    name: string;
  } | null;
  userActions?: { action: string; icon: string; enabled?: boolean }[];
  notifications?: {
    notification_id: number;
    read: boolean;
    student_name: string;
    room_name: string;
    created_at: string;
  }[];
}

export interface StudentProgressExport {
  id: string;
  first_name: string;
  last_name: string;
  progress_percentage: number;
  chapters_completed: number;
  total_chapters: number;
  completed_chapter_names: string[];
  time_on_platform_minutes: number;
}

/**
 * Cabecera del dashboard grupal — `GET /organizations/rooms/<id>/dashboard/`.
 * Shape asumido; ajustar los nombres de campo cuando el backend esté listo.
 */
export interface RoomDashboard {
  name: string;
  students_count: number;
  updated_at?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RoomsService {
  private institutionCache = new Map<string, Observable<any>>();
  private institutionHeaderCache = new Map<
    string,
    Observable<IInstitutionHeader>
  >();

  constructor(
    private httpClient: HttpClient,
    private contentLanguageService: ContentLanguageService,
  ) { }

  getRooms(): Observable<any> {
    const url = environment.apiUrl + '/organizations/rooms/';

    return this.httpClient.get(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Clases de la organización, acotadas al ciclo lectivo vigente.
   *
   * `academic_year=current` lo resuelve el backend por organización: si el
   * colegio no tiene ciclo vigente devuelve todas (es el caso mayoritario, el
   * de los que todavía no hicieron ningún pase de año), y las clases sin ciclo
   * asignado se devuelven siempre. Sin esto el docente sigue viendo las clases
   * del año pasado junto a las nuevas.
   */
  getRoomsListByOrganization(organizationId: number): Observable<IRoom[]> {
    const url =
      environment.apiUrl +
      `/organizations/rooms/list/?organization=${organizationId}` +
      `&academic_year=current`;

    return this.httpClient.get<IRoom[]>(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Download the rooms-list export (CSV) for an organization.
   * Returns the raw Blob; the caller is responsible for triggering the
   * browser download.
   */
  exportRoomsListByOrganization(
    organizationId: number | string,
  ): Observable<Blob> {
    const url =
      environment.apiUrl +
      `/organizations/rooms/list-export/?organization=${organizationId}` +
      `&academic_year=current`;
    return this.httpClient
      .get(url, { responseType: 'blob' })
      .pipe(catchError((error) => throwError(() => error)));
  }

  getRoom(id: string): Observable<any> {
    const lang = this.contentLanguageService.getContentLang();
    const queryParam = `?lang=${lang}`;
    const url =
      environment.apiUrl + '/organizations/rooms/' + id + '/' + queryParam;

    return this.httpClient.get(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  /**
   * Cabecera del dashboard grupal de una sala (nombre + cantidad de alumnos).
   * `GET /organizations/rooms/<id>/dashboard/`
   */
  getRoomDashboard(roomId: string): Observable<RoomDashboard> {
    const url = `${environment.apiUrl}/organizations/rooms/${roomId}/dashboard/`;
    return this.httpClient.get<RoomDashboard>(url);
  }

  /**
   * V3 students endpoint: returns the room metadata together with a
   * paginated students payload of shape `{ count, next, previous, results }`
   * including progress, current_chapter and notifications per student.
   * Used by the redesigned class users list view.
   *
   * `pageSize` is forwarded as `?page_size=` (DRF convention) so the
   * frontend's table size (10) and the backend page size stay in sync.
   * `search` is forwarded as `?search=` (DRF SearchFilter convention)
   * when non-empty so the header search box drives the table query.
   */
  getRoomStudents(
    roomId: string | number,
    page = 1,
    pageSize = 10,
    search = '',
  ): Observable<any> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('page_size', String(pageSize));
    const trimmed = (search ?? '').trim();
    if (trimmed) params.set('search', trimmed);
    const url =
      environment.apiUrl +
      `/organizations/rooms/${roomId}/students/?${params.toString()}`;
    return this.httpClient.get<any>(url).pipe(
      catchError((error) => throwError(() => error)),
    );
  }

  /**
   * Detalle de un alumno del aula. Mismo serializer que `getRoomStudents`, más
   * los bloques `room` y `organization`.
   *
   * El backend valida la relación docente↔aula↔alumno: un `studentId` que no
   * pertenece al aula devuelve 404 aunque exista. No se puede confiar en los
   * ids de la URL para decidir qué mostrar.
   */
  getRoomStudent(
    roomId: string | number,
    studentId: string | number,
  ): Observable<RoomStudentDetail> {
    const lang = this.contentLanguageService.getContentLang();
    const url =
      environment.apiUrl +
      `/organizations/rooms/${roomId}/students/${studentId}/?lang=${lang}`;

    return this.httpClient.get<RoomStudentDetail>(url).pipe(
      catchError((error) => throwError(() => error)),
    );
  }

  getRoomAvailableUsers(roomId: string): Observable<any> {
    const url = `${environment.apiUrl}/organizations/rooms/${roomId}/available-users/`;
    return this.httpClient.get(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  createRoom(room: IRoom): Observable<any> {
    const url = environment.apiUrl + '/organizations/rooms/';

    return this.httpClient.post(url, room).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  updateRoom(room: IRoom): Observable<any> {
    const url = environment.apiUrl + `/organizations/rooms/${room.id}/`;

    return this.httpClient.put(url, room).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  getInstitution(institutionId: string): Observable<any> {
    const lang = this.contentLanguageService.getContentLang();
    const cacheKey = `${institutionId}_${lang}`;

    if (!this.institutionCache.has(cacheKey)) {
      const queryParam = `?lang=${lang}`;
      const url =
        environment.apiUrl +
        '/organizations/' +
        institutionId +
        '/' +
        queryParam;

      const request$ = this.httpClient.get(url).pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError((error) => {
          this.institutionCache.delete(cacheKey);
          return throwError(() => error);
        }),
      );
      this.institutionCache.set(cacheKey, request$);
    }

    return this.institutionCache.get(cacheKey)!;
  }

  getInstitutionHeader(institutionId: string): Observable<IInstitutionHeader> {
    const lang = this.contentLanguageService.getContentLang();
    const cacheKey = `${institutionId}_${lang}`;

    if (!this.institutionHeaderCache.has(cacheKey)) {
      const queryParam = `?lang=${lang}`;
      // Asumimos nuevo endpoint tipo /organizations/:id/header/ o similar
      const url = `${environment.apiUrl}/organizations/${institutionId}/header/${queryParam}`;

      const request$ = this.httpClient.get<IInstitutionHeader>(url).pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError((error) => {
          this.institutionHeaderCache.delete(cacheKey);
          return throwError(() => error);
        }),
      );

      this.institutionHeaderCache.set(cacheKey, request$);
    }

    return this.institutionHeaderCache.get(cacheKey)!;
  }

  clearInstitutionCache(institutionId?: string): void {
    if (institutionId) {
      const lang = this.contentLanguageService.getContentLang();
      this.institutionCache.delete(`${institutionId}_${lang}`);
    } else {
      this.institutionCache.clear();
    }
  }

  requestJoinToRoom(roomCode: { code: string }): Observable<any> {
    const url = environment.apiUrl + '/organizations/rooms/request_to_join/';

    return this.httpClient.post(url, roomCode).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  answerRequestToJoin(
    participantId: string,
    answer: { answer: boolean },
  ): Observable<any> {
    const url =
      environment.apiUrl + '/organizations/answer-request/' + participantId;

    return this.httpClient.post(url, answer).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  getPetitions(roomId: string): Observable<any> {
    const url =
      environment.apiUrl + '/organizations/participant-requests/' + roomId;

    return this.httpClient.get(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  editRoom(room: IRoom): Observable<any> {
    const url = environment.apiUrl + '/organizations/rooms/' + room.id! + '/';

    return this.httpClient.put(url, room).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  deleteRoom(roomId: string): Observable<any> {
    const url = environment.apiUrl + '/organizations/rooms/' + roomId + '/';

    return this.httpClient.delete(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  dropStudentFromRoom(studentId: string, roomId: string) {
    // organizations/remove-user/:id
    const url =
      environment.apiUrl +
      '/organizations/rooms/' +
      roomId +
      '/delete-student/';

    return this.httpClient
      .delete(url, {
        body: {
          student_id: studentId,
        },
      })
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  moveStudent(roomData: any, studentId: string) {
    const url = environment.apiUrl + '/organizations/move-users/' + studentId;

    return this.httpClient.put(url, roomData).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  getInstitutions(): Observable<any> {
    return this.httpClient.get(`${environment.apiUrl}/organizations/`).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  public addParticipant(roomId: string, userId: number) {
    return this.httpClient.post(
      `${environment.apiUrl}/organizations/rooms/${roomId}/add-user/`,
      { user_id: userId },
    );
  }

  getContents(id: string): Observable<any> {
    const lang = this.contentLanguageService.getContentLang();
    const queryParam = `?lang=${lang}`;
    const url =
      environment.apiUrl +
      '/organizations/rooms/' +
      id +
      '/contents/' +
      queryParam;

    return this.httpClient.get(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  toggleContentFromRoom(contentId: string, roomId: string): Observable<any> {
    const url =
      environment.apiUrl +
      '/organizations/rooms/' +
      roomId +
      '/toggle-content/';

    return this.httpClient.post(url, contentId).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  getInstitutionsList(): Observable<any> {
    const url = environment.apiUrl + '/organizations/organizations_list/';
    return this.httpClient.get(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  getRoomLevels(countryId: number): Observable<any> {
    const url = `${environment.apiUrl}/organizations/room-levels/?country=${countryId}`;
    return this.httpClient.get(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  getStudentsProgressExport(roomId: string): Observable<Blob> {
    const lang = this.contentLanguageService.getContentLang();
    const url = `${environment.apiUrl}/organizations/rooms/${roomId}/students-progress/?lang=${lang}`;

    return this.httpClient.get(url, {
      responseType: 'blob'
    }).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }
}
